import { del, get, post } from '@/shared/utils/FetchV2.util';
import { fiscalEngineOptions } from './fiscalEngineLocal';

/**
 * Custodia del sello digital en `heru-fiscal-engine` (Go), que releva a
 * `heru-core-invoice-service` en este dominio.
 *
 * El backend deriva RFC, nombre, serie y vigencia del propio certificado, así
 * que el formulario NO los pide: pedirlos invita a que no cuadren con el
 * archivo. Y el `message` de los errores viene redactado en español para el
 * usuario final — se muestra tal cual, no se traduce ni se sustituye por un
 * genérico.
 */
export interface CsdSeal {
  user_id: number;
  rfc: string;
  /**
   * `null` en los sellos que subió el flujo legacy: invoice-service guarda el
   * certificado sin su número de serie (columna `certificate_id`).
   */
  serial_number: string | null;
  name: string;
  /** Viene vacío en los sellos que registra este servicio: el lugar de expedición sale del perfil fiscal. */
  postal_code: string | null;
  valid_from: string | null;
  valid_to: string | null;
  /** Compara la vigencia contra el reloj del servidor: un sello puede existir y estar vencido. */
  is_valid: boolean;
  storage: string;
  created_at: string;
  /**
   * Si el SAT ya acepta el sello para timbrar. Solo la manda `GET /csd/seal` del
   * fiscal-engine desde que existe: sin ella se trata como `unknown`.
   */
  activation?: CsdSealActivation;
}

/**
 * `pending`: el SAT todavía no lo acepta. Por `new_certificate` (emitido hace menos
 * de 24 h y sin timbrar) o por `lco_rejection` (el PAC lo rechazó porque el RFC no
 * está en la lista del SAT, y no ha timbrado después). Es un aviso, no un candado.
 */
export interface CsdSealActivation {
  status: 'pending' | 'active' | 'unknown';
  reason: 'new_certificate' | 'lco_rejection' | null;
  since: string | null;
}

/**
 * Sección desde la que se sube el sello. Solo mide: viaja a la prop `surface` de
 * `invoice_v2_csd_registered`, que emite el fiscal-engine. Cada lugar que monta
 * el cargador pasa la suya explícita; no se deduce de la URL. Un valor que el
 * backend no conoce llega como `desconocida` y no rompe el alta.
 */
export type CsdSealSurface =
  | 'facturacion'
  | 'perfil_fiscal'
  | 'onboarding'
  | 'drawer_fiscal'
  | 'facturacion_legacy';

export interface UploadCsdSealPayload {
  /** Contenido DER del `.cer` en base64, sin encabezados PEM ni prefijo data-url. */
  certificate: string;
  /** Contenido DER del `.key` en base64. */
  privateKey: string;
  password: string;
  surface: CsdSealSurface;
  /**
   * What an account with no SAT profile types next to its seal: the seal carries the
   * RFC, not the regime nor the domicile. Only sent when the backend asked for it
   * (`CSD_FALTAN_DATOS_FISCALES`, then `previewCsdSealService`).
   */
  issuer?: CsdIssuerData;
}

export interface CsdIssuerData {
  tax_regime: string;
  postal_code: string;
  /** Empty: the certificate's name. */
  name?: string;
}

/** A regime offered to an issuer with no SAT profile, for the RFC's person type. */
export interface CsdIssuerRegime {
  code: string;
  name: string;
  short_name: string;
}

/**
 * `POST /csd/seal/preview`: every check of the upload, nothing written. Says what
 * the seal is and whether the upload must carry `issuer`.
 */
export interface CsdSealPreview {
  rfc: string;
  /** The certificate's name, proposed as the issuer's. */
  name: string;
  person_type: 'fisica' | 'moral';
  valid_from: string;
  valid_to: string;
  /** `seal`: the account has no SAT profile and takes the seal's RFC. */
  rfc_source: 'profile' | 'seal';
  issuer_data_required: boolean;
  /** What the account already typed for this RFC, to prefill. */
  issuer?: CsdIssuerData & { sat_validation: 'validated' | 'unverified' };
  regimes: CsdIssuerRegime[];
}

export type UploadCsdSealResult =
  | { status: 'created'; seal: CsdSeal }
  | { status: 'already_exists'; message: string };

const CSD_SEAL_ENDPOINT = '/csd/seal';

export const CSD_SEAL_QUERY_KEY = ['invoicing', 'csd-seal'];

type ErrorBody = {
  response?: {
    status?: number;
    data?: { message?: unknown; code?: unknown; details?: { field?: unknown } };
  };
};

const errorStatus = (error: unknown): number | undefined => (error as ErrorBody)?.response?.status;

const conflictOf = (value: unknown, code: string): { message: string } | null => {
  if (typeof value !== 'object' || value === null) return null;
  const body = value as { statusCode?: number; code?: string; message?: string };
  if (body.code !== code && body.statusCode !== 409) return null;
  return { message: body.message ?? '' };
};

/**
 * Un 409 llega por dos caminos y los dos se atienden: con `skipAccountError`
 * (el caso de estos servicios) `FetchV2` LANZA el error; sin él,
 * `onAccountError` devuelve el cuerpo como si fuera una respuesta buena. Solo
 * se reconoce el `code` pedido: cualquier otro error sigue su camino.
 */
const settleConflict = async <T>(
  request: () => Promise<T>,
  code: string,
): Promise<{ ok: true; value: T } | { ok: false; message: string }> => {
  try {
    const value = await request();
    const conflict = conflictOf(value, code);
    return conflict ? { ok: false, message: conflict.message } : { ok: true, value };
  } catch (error) {
    const data = (error as ErrorBody)?.response?.data;
    if (errorStatus(error) === 409 && data?.code === code) {
      return { ok: false, message: typeof data.message === 'string' ? data.message : '' };
    }
    throw error;
  }
};

/**
 * Un sello ya registrado no se sobrescribe: el backend responde 409 a cualquier
 * sello existente, vencido incluido, y el flujo correcto es mostrarlo, no
 * volver a pedirlo.
 */
export const uploadCsdSealService = async (
  payload: UploadCsdSealPayload,
): Promise<UploadCsdSealResult> => {
  const result = await settleConflict(
    () =>
      post<{ message: string; seal: CsdSeal }>({
        service: 'fiscalEngine',
        endpoint: CSD_SEAL_ENDPOINT,
        options: fiscalEngineOptions(CSD_SEAL_ENDPOINT),
        body: { type: 'csd', ...payload },
        // Un 401 del fiscal-engine local no debe borrar el token ni mandar la app
        // entera al login: el JWT de prod puede no validar contra el servicio local.
        skipAccountError: true,
      }),
    'CSD_YA_REGISTRADO',
  );
  return result.ok
    ? { status: 'created', seal: result.value.seal }
    : { status: 'already_exists', message: result.message };
};

const CSD_SEAL_PREVIEW_ENDPOINT = '/csd/seal/preview';

/** Same body as the upload without `type`, `replace` nor `issuer`; same error codes. */
export const previewCsdSealService = (
  payload: Omit<UploadCsdSealPayload, 'issuer'>,
): Promise<CsdSealPreview> =>
  post<CsdSealPreview>({
    service: 'fiscalEngine',
    endpoint: CSD_SEAL_PREVIEW_ENDPOINT,
    options: fiscalEngineOptions(CSD_SEAL_PREVIEW_ENDPOINT),
    body: payload,
    skipAccountError: true,
  });

/**
 * Baja de TODOS los sellos del usuario. Es la única forma de cambiar uno: el
 * alta responde 409 a cualquier sello registrado, vencido incluido.
 */
export const deleteCsdSealService = (): Promise<unknown> =>
  del({
    service: 'fiscalEngine',
    endpoint: CSD_SEAL_ENDPOINT,
    options: fiscalEngineOptions(CSD_SEAL_ENDPOINT),
    skipAccountError: true,
  });

export type CsdSealFile = 'cer' | 'key';

/**
 * Enlaces firmados (5 min) del `.cer` y el `.key` del sello del usuario del
 * token. Salen siempre del bucket privado del sello, nunca de la URL guardada
 * en el perfil, y el navegador los guarda como archivo. Se piden al tocar
 * descargar: un enlace pedido antes puede estar vencido.
 */
export interface CsdSealFiles {
  cer_url: string;
  key_url: string;
  expires_in: number;
}

const CSD_SEAL_FILES_ENDPOINT = '/csd/seal/files';

/**
 * `404` con `CSD_NO_REGISTRADO` (sin sello) o `CSD_ARCHIVO_NO_ENCONTRADO` (el
 * archivo no está en el bucket). Lanza en cualquier error: no hay URL de
 * respaldo a la que caer.
 */
export const getCsdSealFilesService = (): Promise<CsdSealFiles> =>
  get<CsdSealFiles>({
    service: 'fiscalEngine',
    endpoint: CSD_SEAL_FILES_ENDPOINT,
    options: fiscalEngineOptions(CSD_SEAL_FILES_ENDPOINT),
    skipAccountError: true,
  });

/** `404` es el estado inicial normal —no hay sello—, no un error que reportar. */
export const getCsdSealService = async (): Promise<CsdSeal | null> => {
  try {
    return await get<CsdSeal>({
      service: 'fiscalEngine',
      endpoint: CSD_SEAL_ENDPOINT,
      options: fiscalEngineOptions(CSD_SEAL_ENDPOINT),
      skipAccountError: true,
    });
  } catch (error) {
    if (errorStatus(error) === 404) return null;
    throw error;
  }
};

/** Trámite de un CSD nuevo ante el SAT con la e.firma del usuario (`GET /csd/status`). */
export interface CsdTramite {
  estado: 'en_proceso' | 'completado' | 'error';
  rfc: string | null;
  seal_name: string | null;
  /** Con serie, el certificado ya existe en el SAT aunque el trámite haya fallado después. */
  serial: string | null;
  numero_operacion: string | null;
  cer_url: string | null;
  vigente_desde: string | null;
  vigente_hasta: string | null;
  /** El sello generado ya quedó registrado para timbrar. */
  invoice_service_uploaded: boolean;
  workflow_id: string | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const CSD_STATUS_ENDPOINT = '/csd/status';
const CSD_GENERATE_ENDPOINT = '/csd/generate';

export const CSD_STATUS_QUERY_KEY = ['invoicing', 'csd-tramite'];

/** `404` (`CSD_SIN_TRAMITE`) = nunca ha tramitado un sello: estado inicial, no error. */
export const getCsdStatusService = async (): Promise<CsdTramite | null> => {
  try {
    return await get<CsdTramite>({
      service: 'fiscalEngine',
      endpoint: CSD_STATUS_ENDPOINT,
      options: fiscalEngineOptions(CSD_STATUS_ENDPOINT),
      skipAccountError: true,
    });
  } catch (error) {
    if (errorStatus(error) === 404) return null;
    throw error;
  }
};

export type GenerateCsdResult =
  | { status: 'started'; workflowId: string }
  | { status: 'already_running'; message: string };

/**
 * Pide al SAT un CSD nuevo con la e.firma del usuario.
 *
 * ⚠️ NO es idempotente: cada llamada que el backend acepta crea un certificado
 * real en el SAT. Nunca se reintenta sola, y quien la llama bloquea el botón
 * mientras está pendiente o mientras haya un trámite `en_proceso`. El body va
 * vacío: un `variant` no vacío hoy truena con 422 en extractions.
 */
export const generateCsdService = async (): Promise<GenerateCsdResult> => {
  const result = await settleConflict(
    () =>
      post<{ workflow_id: string; status: string }>({
        service: 'fiscalEngine',
        endpoint: CSD_GENERATE_ENDPOINT,
        options: fiscalEngineOptions(CSD_GENERATE_ENDPOINT),
        body: {},
        skipAccountError: true,
      }),
    'CSD_TRAMITE_EN_PROCESO',
  );
  return result.ok
    ? { status: 'started', workflowId: result.value.workflow_id }
    : { status: 'already_running', message: result.message };
};

/**
 * Código estable del backend. Es el ÚNICO campo por el que se debe ramificar:
 * el `message` puede reescribirse o traducirse sin aviso, y comparar cadenas ata
 * la UI a la redacción — el día que alguien mejore un texto, el front deja de
 * reconocer el error y nada truena.
 */
export const csdSealErrorCode = (error: unknown): string | null => {
  const code = (error as ErrorBody)?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
};

/** The field an issuer-data rejection points at (`details.field`), or `null`. */
export const csdSealErrorField = (error: unknown): string | null => {
  const field = (error as ErrorBody)?.response?.data?.details?.field;
  return typeof field === 'string' && field ? field : null;
};

/** Mensaje del backend, redactado corto y para el usuario final. */
export const csdSealErrorMessage = (
  error: unknown,
  fallback = 'No pudimos validar tus sellos. Inténtalo de nuevo.',
): string => {
  const message = (error as ErrorBody)?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Lee el archivo como base64 del binario crudo (DER). `readAsDataURL` codifica
 * los bytes tal cual; `readAsText` los corrompe en silencio y el backend
 * responde «el archivo .cer no es un certificado válido», que manda a buscar el
 * problema donde no está.
 */
export const fileToDerBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error);
  });
